import React, { useState, useEffect, createContext, useContext } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'success',
      duration: 5000,
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    if (newNotification?.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification?.duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev?.filter(notification => notification?.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success: (message, options = {}) => addNotification({ ...options, type: 'success', message }),
    error: (message, options = {}) => addNotification({ ...options, type: 'error', message }),
    warning: (message, options = {}) => addNotification({ ...options, type: 'warning', message }),
    info: (message, options = {}) => addNotification({ ...options, type: 'info', message }),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = () => {
  const { notifications } = useNotification();

  return (
    <div className="fixed top-20 right-6 z-400 space-y-3 max-w-sm w-full">
      {notifications?.map((notification) => (
        <NotificationToast key={notification?.id} notification={notification} />
      ))}
    </div>
  );
};

const NotificationToast = ({ notification }) => {
  const { removeNotification } = useNotification();
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      removeNotification(notification?.id);
    }, 200);
  };

  const getNotificationStyles = () => {
    const baseStyles = "border shadow-elevation-2";
    
    switch (notification?.type) {
      case 'success':
        return `${baseStyles} bg-success text-success-foreground border-success/20`;
      case 'error':
        return `${baseStyles} bg-error text-error-foreground border-error/20`;
      case 'warning':
        return `${baseStyles} bg-warning text-warning-foreground border-warning/20`;
      case 'info':
        return `${baseStyles} bg-accent text-accent-foreground border-accent/20`;
      default:
        return `${baseStyles} bg-card text-card-foreground border-border`;
    }
  };

  const getIcon = () => {
    switch (notification?.type) {
      case 'success':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'info':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out
        ${isVisible && !isRemoving 
          ? 'translate-x-0 opacity-100 scale-100' :'translate-x-full opacity-0 scale-95'
        }
      `}
    >
      <div className={`
        rounded-lg p-4 flex items-start space-x-3 min-w-0
        ${getNotificationStyles()}
        animate-slide-up
      `}>
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <Icon name={getIcon()} size={20} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {notification?.title && (
            <h4 className="text-sm font-semibold mb-1">
              {notification?.title}
            </h4>
          )}
          
          <p className="text-sm opacity-90">
            {notification?.message}
          </p>

          {notification?.description && (
            <p className="text-xs opacity-75 mt-1">
              {notification?.description}
            </p>
          )}

          {notification?.action && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs hover:bg-white/10"
                onClick={() => {
                  notification?.action?.onClick();
                  handleRemove();
                }}
              >
                {notification?.action?.label}
              </Button>
            </div>
          )}
        </div>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0 w-6 h-6 hover:bg-white/10 opacity-70 hover:opacity-100"
          onClick={handleRemove}
          iconName="X"
          iconSize={14}
        >
          <span className="sr-only">Close notification</span>
        </Button>
      </div>
      {/* Progress Bar */}
      {notification?.duration > 0 && (
        <div className="h-1 bg-black/10 rounded-b-lg overflow-hidden">
          <div 
            className="h-full bg-white/30 rounded-b-lg"
            style={{
              animation: `notification-progress ${notification?.duration}ms linear forwards`,
              width: '100%'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationToast;